import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { debounce } from 'lodash';
import css from './ShopSettingsForm.module.css';
import { Icons } from '../../../components';
import { SharetribeApi } from '../../../util/api';
import { StatusCode } from '../../../util/enums';
import { useDispatch } from 'react-redux';
import { updateProfile } from '../../ProfileSettingsPage/ProfileSettingsPage.duck';

const UserNameSection = ({ intl }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isAvailable, setIsAvailable] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null); // Reference to store timeout ID

  // Debounced function to check username availability and save if available
  const checkAvailability = useCallback(
    debounce(async username => {
      if (!username || username.length < 3) {
        setIsAvailable(null);
        setError(null);
        return;
      }
      try {
        setIsChecking(true);
        const response = await SharetribeApi.checkUsernameAvailability({ username });
        if (response.statusCode === StatusCode.SUCCESS) {
          setIsAvailable(true);
          setError(null);
          // Automatically save the profile
          await dispatch(
            updateProfile({
              publicData: {
                username,
              },
            })
          );
          // Clear any existing timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          // Set new timeout to close input box
          timeoutRef.current = setTimeout(() => {
            setIsEditing(false); // Close input box after successful save
            timeoutRef.current = null; // Clear timeout reference
          }, 500); // 500ms for snappier feel
        } else {
          setIsAvailable(false);
          setError('.Error.NotAvailableUserNameSection');
        }
      } catch (err) {
        setError(err.message || 'UserNameSection.Error.CheckingAvailability');
        setIsAvailable(null);
      } finally {
        setIsChecking(false);
      }
    }, 500),
    [dispatch]
  );

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle input change
  const handleUsernameChange = input => e => {
    const username = e.target.value;
    input.onChange(username);
    // Cancel any pending timeout if user types again
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    checkAvailability(username);
  };

  // Toggle edit mode
  const toggleEdit = () => {
    setIsEditing(!isEditing);
    setIsAvailable(null);
    setError(null);
    // Clear timeout when exiting edit mode manually
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  return (
    <div className={css.usernameSection}>
      <Field name="username">
        {({ input }) => (
          <>
            {isEditing ? (
              <div className={css.inputWrapper}>
                <input
                  type="text"
                  className={css.usernameInput}
                  placeholder={intl.formatMessage({ id: 'UserNameSection.Placeholder' })}
                  {...input}
                  onChange={handleUsernameChange(input)}
                  autoFocus
                />
                <div className={css.status}>
                  {isChecking && (
                    <span className={css.checking}>
                      <FormattedMessage id="UserNameSection.Checking" />
                    </span>
                  )}
                  {isAvailable === true && (
                    <span className={css.available}>
                      <FormattedMessage id="UserNameSection.Available" />
                    </span>
                  )}
                  {isAvailable === false && (
                    <span className={css.taken}>
                      <FormattedMessage id="UserNameSection.Taken" />
                    </span>
                  )}
                  {error && (
                    <span className={css.error}>
                      <FormattedMessage id={error} />
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <span className={css.username}>
                {input.value || <FormattedMessage id="UserNameSection.DefaultUsername" />}
              </span>
            )}
          </>
        )}
      </Field>
      {/* <span
        className={css.editIcon}
        onClick={toggleEdit}
        aria-label={intl.formatMessage({
          id: isEditing ? 'UserNameSection.SaveUsername' : 'UserNameSection.EditUsername',
        })}
      >
        <Icons name={isEditing ? 'check' : 'pencil'} />
      </span> */}
    </div>
  );
};

export default UserNameSection;
